import { getApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaEnterpriseProvider, getToken } from "firebase/app-check";
import { onAuthStateChanged } from "firebase/auth";

// Firebase App Check en el portal (plan-seguridad-alto-nivel-2026-Q4, control 3).
// MODO OBSERVACIÓN: el backend registra si el token llega y es válido, pero no bloquea
// (APP_CHECK_ENFORCE en functions/middlewares/appCheck.middleware.js).
//
// Proveedor: reCAPTCHA Enterprise. REACT_APP_RECAPTCHA_SITE_KEY es una clave Enterprise
// de puntuación: el front carga recaptcha/enterprise.js y llama grecaptcha.enterprise.execute
// (useRecaptchaEnterprise.js, recaptchaUtils.js) y el backend la evalúa con
// @google-cloud/recaptcha-enterprise createAssessment en el mismo proyecto de Firebase.
//
// Solo se inicializa con sesión iniciada (primer onAuthStateChanged con usuario, o la
// primera petición autenticada de functionsClient). Motivos:
//   - la exigencia futura es solo para el portal autenticado (routers V2 y callables);
//   - cada visitante anónimo de la landing, el blog, el quiz o las reservas costaría una
//     evaluación de reCAPTCHA sin servir para nada (10 000 gratis al mes);
//   - los formularios públicos cargan su propio reCAPTCHA; así no se suma otro script.
//
// Si algo falla (sin clave, SDK, red, reCAPTCHA bloqueado por una extensión), el portal
// sigue igual: las peticiones salen sin la cabecera y el backend las cuenta como "missing".
//
// Token de depuración: REACT_APP_APPCHECK_DEBUG ("true" para que el SDK genere uno y lo
// imprima en la consola, o el UUID registrado en la consola de Firebase). Nunca en el
// proyecto de producción, aunque la variable esté definida. Ponerla solo en
// .env.development.local (npm start); build:testing no lee ese archivo.

export const APP_CHECK_HEADER = "X-Firebase-AppCheck";
export const PRODUCTION_PROJECT_ID = "goshofi-c578e";
// Primera espera: carga de reCAPTCHA + intercambio del token. Después de un fallo, la
// espera baja para no penalizar cada petición mientras App Check no responde.
export const FIRST_TOKEN_WAIT_MS = 3000;
export const DEGRADED_TOKEN_WAIT_MS = 250;

let appCheckInstance = null;
let initAttempted = false;
let degraded = false;

// Referencias explícitas: pasar `process.env` entero metería en el bundle todas las
// variables REACT_APP_*, también las que el código no usa.
const readEnv = () => ({
  REACT_APP_RECAPTCHA_SITE_KEY: process.env.REACT_APP_RECAPTCHA_SITE_KEY,
  REACT_APP_APPCHECK_DEBUG: process.env.REACT_APP_APPCHECK_DEBUG,
  REACT_APP_FIREBASE_PROJECT_ID: process.env.REACT_APP_FIREBASE_PROJECT_ID,
});

// true | "<uuid>" | undefined (sin depuración).
export function resolveDebugToken(env = readEnv()) {
  const raw = String(env.REACT_APP_APPCHECK_DEBUG || "").trim();
  if (!raw || raw.toLowerCase() === "false") return undefined;
  if (env.REACT_APP_FIREBASE_PROJECT_ID === PRODUCTION_PROJECT_ID) return undefined;
  return raw.toLowerCase() === "true" ? true : raw;
}

// Inicializa App Check una sola vez. Nunca lanza; devuelve la instancia o null.
export function initAppCheck(app, env = readEnv()) {
  if (initAttempted) return appCheckInstance;
  initAttempted = true;
  try {
    if (typeof window === "undefined") return null;
    const siteKey = env.REACT_APP_RECAPTCHA_SITE_KEY;
    if (!siteKey) {
      console.warn("[AppCheck] Falta REACT_APP_RECAPTCHA_SITE_KEY: el portal sigue sin App Check.");
      return null;
    }
    const debugToken = resolveDebugToken(env);
    if (debugToken !== undefined) {
      window.FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken;
    }
    appCheckInstance = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(siteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (err) {
    appCheckInstance = null;
    console.warn("[AppCheck] No se pudo inicializar: el portal sigue sin App Check.", err?.code || err?.message);
  }
  return appCheckInstance;
}

// Inicializa App Check con el primer usuario con sesión. Nunca lanza.
export function initAppCheckOnSession(app, auth) {
  try {
    let done = false;
    let unsubscribe = null;
    unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user || done) return;
      done = true;
      initAppCheck(app);
      if (typeof unsubscribe === "function") unsubscribe();
    });
    if (done && typeof unsubscribe === "function") unsubscribe();
  } catch (err) {
    console.warn("[AppCheck] No se pudo esperar la sesión: el portal sigue sin App Check.", err?.code || err?.message);
  }
}

function ensureAppCheck() {
  if (initAttempted) return appCheckInstance;
  try {
    return initAppCheck(getApp());
  } catch (_err) {
    return null;
  }
}

function withTimeout(promise, ms) {
  let timer;
  const timeout = new Promise((_resolve, reject) => {
    timer = setTimeout(() => reject(new Error("app-check-timeout")), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

// Cabecera X-Firebase-AppCheck lista para axios, o {} si no hay token. Nunca lanza.
// Usa el token en caché del SDK (no fuerza renovación: cada renovación es una evaluación).
export async function getAppCheckHeaders() {
  const instance = ensureAppCheck();
  if (!instance) return {};
  try {
    const result = await withTimeout(
      getToken(instance, false),
      degraded ? DEGRADED_TOKEN_WAIT_MS : FIRST_TOKEN_WAIT_MS
    );
    degraded = false;
    return result?.token ? { [APP_CHECK_HEADER]: result.token } : {};
  } catch (_err) {
    degraded = true;
    return {};
  }
}
